"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowLeft,
  ArrowUp,
  Bot,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Database,
  User,
} from "lucide-react";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  sources?: Source[];
}

interface Source {
  sourceId: string;
  sourceType: string;
  chunkText: string;
  title: string;
  distance: number;
}

const JIRA_BASE = "https://ac-avi.atlassian.net";

function linkJiraKeys(text: string): string {
  return text.replace(
    /(?<![\[\(\/])([A-Z]{2,}-\d+)(?![\]\)])/g,
    `[$1](${JIRA_BASE}/browse/$1)`,
  );
}

const style = (
  <style>{`
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `}</style>
);

function TypingDots() {
  return (
    <div className="flex items-center gap-1.5 px-1">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-2 w-2 animate-bounce rounded-full"
          style={{
            backgroundColor: i === 0 ? "#60a5fa" : i === 1 ? "#3b82f6" : "#818cf8",
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  );
}

export default function ChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [foundSources, setFoundSources] = useState(0);
  const [expandedSources, setExpandedSources] = useState<Record<number, boolean>>({});
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const sendMessage = useCallback(async () => {
    const question = input.trim();
    if (!question || loading) return;

    const userMsg: ChatMessage = { role: "user", content: question };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    setIsWaiting(true);
    setFoundSources(0);

    let assistantAdded = false;

    try {
      const r = await fetch("/api/kb/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });

      if (!r.ok) {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: "Something went wrong. Please try again." },
        ]);
        setLoading(false);
        setIsWaiting(false);
        return;
      }

      const reader = r.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let answer = "";
      let currentSources: Source[] = [];

      function updateAssistant(content: string, sources: Source[]) {
        if (!assistantAdded) {
          assistantAdded = true;
          setMessages((prev) => [...prev, { role: "assistant", content, sources }]);
        } else {
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: "assistant", content, sources };
            return next;
          });
        }
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const data = JSON.parse(line);
            if (data.type === "token") {
              setIsWaiting(false);
              answer += data.text;
              updateAssistant(answer, currentSources);
            } else if (data.type === "sources") {
              currentSources = data.sources ?? [];
              setFoundSources(currentSources.length);
            } else if (data.type === "error") {
              answer += "\n\n(Stream interrupted)";
              updateAssistant(answer, currentSources);
            }
          } catch {
            // skip malformed lines
          }
        }
      }

      if (!assistantAdded) {
        updateAssistant(answer, currentSources);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong. Please try again." },
      ]);
    }
    setLoading(false);
    setIsWaiting(false);
  }, [input, loading]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  function toggleSources(idx: number) {
    setExpandedSources((prev) => ({ ...prev, [idx]: !prev[idx] }));
  }

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      {style}
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
        <Link
          href="/kb"
          className="flex items-center gap-2.5 group"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 transition group-hover:from-blue-600 group-hover:to-indigo-700">
            <Bot className="h-4 w-4 text-white" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-gray-900 transition group-hover:text-blue-600">
              Ask the KB
            </h1>
            <p className="text-xs text-gray-400 transition group-hover:text-gray-500">
              AI-powered knowledge base search
            </p>
          </div>
        </Link>
        <Link
          href="/kb"
          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-500 transition hover:border-gray-300 hover:text-gray-800 hover:shadow-sm"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Browse KB
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-6">
          {messages.length === 0 ? (
            <div className="flex h-full min-h-[60vh] flex-col items-center justify-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 shadow-sm">
                <BookOpen className="h-7 w-7 text-blue-500" />
              </div>
              <h2 className="mt-5 text-xl font-semibold text-gray-900">
                Ask me anything
              </h2>
              <p className="mt-1.5 max-w-sm text-sm text-gray-500">
                I can search the knowledge base and answer your questions with citations.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-2">
                {[
                  "What is the EUV roadmap?",
                  "How does H2D work?",
                  "Summarize the latest sprint",
                ].map((q) => (
                  <button
                    key={q}
                    onClick={() => {
                      setInput(q);
                      setTimeout(() => sendMessage(), 0);
                    }}
                    className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-600 shadow-sm transition hover:border-blue-200 hover:text-blue-600"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "animate-[fadeIn_0.3s_ease-out] justify-start"}`}
                >
                  <div
                    className={`flex max-w-[80%] gap-3 ${
                      msg.role === "user" ? "flex-row-reverse" : "flex-row"
                    }`}
                  >
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ${
                        msg.role === "user"
                          ? "bg-gray-800"
                          : "border border-gray-200 bg-white shadow-sm"
                      }`}
                    >
                      {msg.role === "user" ? (
                        <User className="h-4 w-4 text-white" />
                      ) : (
                        <Bot className="h-4 w-4 text-blue-500" />
                      )}
                    </div>
                    <div>
                      <div
                        className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                          msg.role === "user"
                            ? "bg-gray-800 text-white"
                            : "border border-gray-200 bg-white shadow-sm"
                        }`}
                      >
                        {msg.role === "user" ? (
                          <p className="whitespace-pre-wrap text-gray-50">{msg.content}</p>
                        ) : msg.content && loading && !isWaiting && i === messages.length - 1 ? (
                          <div className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
                            {msg.content}
                            <span className="inline-block h-4 w-[2px] animate-pulse bg-blue-500 ml-0.5 align-text-bottom" />
                          </div>
                        ) : (
                          <div className="prose prose-sm prose-gray max-w-none prose-headings:font-semibold prose-headings:text-gray-800 prose-p:text-gray-700 prose-a:text-blue-600 prose-code:rounded prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:text-xs prose-code:font-normal prose-code:text-gray-800 prose-pre:rounded-xl prose-pre:border prose-pre:border-gray-200 prose-pre:bg-gray-50 prose-li:text-gray-700 prose-strong:text-gray-800 dark:prose-invert dark:prose-code:bg-gray-800 dark:prose-code:text-gray-200 dark:prose-pre:border-gray-700 dark:prose-pre:bg-gray-900">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                a: ({ href, children }) => (
                                  <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline decoration-blue-200 underline-offset-2 transition hover:decoration-blue-600"
                                  >
                                    {children}
                                  </a>
                                ),
                              }}
                            >
                              {linkJiraKeys(msg.content)}
                            </ReactMarkdown>
                          </div>
                        )}
                      </div>

                      {msg.sources && msg.sources.length > 0 && (
                        <div className="mt-2">
                          <button
                            onClick={() => toggleSources(i)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-xs text-gray-500 transition hover:border-gray-300 hover:text-gray-700"
                          >
                            <Database className="h-3 w-3" />
                            {msg.sources.length} source{msg.sources.length > 1 ? "s" : ""}
                            {expandedSources[i] ? (
                              <ChevronUp className="h-3 w-3" />
                            ) : (
                              <ChevronDown className="h-3 w-3" />
                            )}
                          </button>
                          {expandedSources[i] && (
                            <div className="mt-2 space-y-2">
                              {msg.sources.map((s, si) => (
                                <div
                                  key={si}
                                  className="rounded-xl border border-gray-100 bg-white px-3.5 py-2.5 text-xs shadow-sm"
                                >
                                  <Link
                                    href={`/kb/${s.sourceId}`}
                                    className="font-medium text-gray-800 transition hover:text-blue-600"
                                  >
                                    {s.title}
                                  </Link>
                                  <span className="ml-2 text-gray-400">
                                    · {Math.min(99, Math.max(1, Math.round((1 - s.distance) * 100)))}% match
                                  </span>
                                  <p className="mt-1 line-clamp-2 text-gray-500">
                                    {s.chunkText}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {isWaiting && (
                <div className="flex justify-start">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white shadow-sm">
                      <Bot className="h-4 w-4 text-blue-500" />
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-3.5 shadow-sm">
                      <div className="flex items-center gap-3">
                        <TypingDots />
                        {foundSources > 0 && (
                          <span className="animate-[fadeIn_0.3s_ease-out] text-xs text-gray-400">
                            {foundSources} source{foundSources > 1 ? "s" : ""} found
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          )}
        </div>
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-gray-200 bg-white px-6 py-4">
        <div className="mx-auto max-w-4xl">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about the knowledge base..."
              disabled={loading}
              className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-800 outline-none transition placeholder:text-gray-400 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-100 disabled:opacity-50"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gray-800 text-white transition hover:bg-gray-700 active:scale-95 disabled:opacity-30"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2.5 text-center text-xs text-gray-400">
            Answers are based on the knowledge base content. Verify important information.
          </p>
        </div>
      </div>
    </div>
  );
}
